<script setup lang="ts">
import { TABLE_BODY_TYPE } from "./useTable";
import { useTableBody } from "./useTableBody";

/**
 * **TableBody** (`e-table-body`)
 *
 * Renders the body section of a `<e-table>`, containing data rows.
 * Supports vertical scrolling when the parent table has `scroll` and
 * `max-height` props set.
 *
 * This component is a structural wrapper — it holds no selection or
 * sorting logic. All state is managed by the `useTable` composable.
 *
 * @example
 * ```html
 * <e-table>
 *   <e-table-body>
 *     <e-table-row>
 *       <e-table-cell>Cell 1</e-table-cell>
 *     </e-table-row>
 *   </e-table-body>
 * </e-table>
 *
 * <!-- With vertical scrolling -->
 * <e-table scroll="vertical" max-height="300px">
 *   <e-table-body>
 *     <!-- rows... -->
 *   </e-table-body>
 * </e-table>
 * ```
 */
defineOptions({
  name: TABLE_BODY_TYPE,
});

const { classes, bodyMaxHeightStyle } = useTableBody();
</script>

<style lang="css">
/* Ensure the host does not interfere with the CSS grid layout of the parent table. */
:host {
  display: contents;
}
</style>

<template>
  <div part="body" :class="classes" :style="bodyMaxHeightStyle">
    <slot />
  </div>
</template>
